/**
 * Admin Management (管理者管理) — inline form + registered list.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdminDataTable from './adminDataTable';
import {
  createAdmin,
  deleteAdmin,
  fetchAdminList,
  resetAdminPassword,
  updateAdmin,
} from '../../../services/adminManagementApi';
import '../empManagement/newRegistration/NewRegistrationModal.css';
import './adminManagement.css';
import addNewIcon from '../../../assests/icons/addNew_icon.png';

export interface AdminAccount {
  id: string;
  name: string;
  email: string;
  contactNumber: string;
}

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isValidJapanesePhone = (value: string): boolean => {
  const digitsOnly = value.replace(/\D/g, '');
  return /^0\d{9,10}$/.test(digitsOnly) && (digitsOnly.length === 10 || digitsOnly.length === 11);
};

/** Strip formatting; keep leading 0 for API (do not use parseInt). */
const normalizePhoneDigits = (value: string): string => value.replace(/\D/g, '');

const AdminManagement: React.FC = () => {
  const formRef = useRef<HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    contactNumber: '',
    email: '',
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  const [resetPasswordResult, setResetPasswordResult] = useState<string | null>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  const loadAdmins = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    fetchAdminList()
      .then((rows) => {
        setAdmins(
          rows.map((row) => ({
            id: row.emailaddress,
            name: row.name,
            email: row.emailaddress,
            contactNumber: String(row.contactnumber ?? ''),
          }))
        );
      })
      .catch(() => {
        setAdmins([]);
        setLoadError('管理者一覧の読み込みに失敗しました。');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const filteredAdmins = admins.filter((admin) => {
    const q = searchQuery.toLowerCase();
    return admin.name.toLowerCase().includes(q) || admin.email.toLowerCase().includes(q);
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClear = () => {
    setFormData({ name: '', contactNumber: '', email: '' });
    setEditingId(null);
    setCreatedPassword(null);
    setResetPasswordResult(null);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.contactNumber || !formData.email) {
      alert('すべてのフィールドを入力してください');
      return;
    }
    if (!isValidEmail(formData.email)) {
      alert('有効なメールアドレスを入力してください');
      return;
    }
    if (!isValidJapanesePhone(formData.contactNumber)) {
      alert('有効な電話番号を入力してください（0から始まる10桁または11桁）');
      return;
    }
    if (isSubmitting) return;

    const contactnumber = normalizePhoneDigits(formData.contactNumber);
    setIsSubmitting(true);
    if (!editingId) {
      setCreatedPassword(null);
    }
    try {
      if (editingId) {
        await updateAdmin(editingId, { name: formData.name, contactnumber });
        loadAdmins();
        setEditingId(null);
        setResetPasswordResult(null);
        setFormData({ name: '', contactNumber: '', email: '' });
      } else {
        const saved = await createAdmin({
          name: formData.name,
          emailaddress: formData.email,
          contactnumber,
        });
        setCreatedPassword(saved.password || null);
        loadAdmins();
        setFormData({ name: '', contactNumber: '', email: '' });
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'EMAIL_EXISTS') {
        alert('このメールアドレスは既に登録されています');
      } else {
        alert(editingId ? '更新に失敗しました' : '登録に失敗しました');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editingId) return;
    setIsResettingPassword(true);
    setResetPasswordResult(null);
    try {
      const newPassword = await resetAdminPassword(editingId);
      if (newPassword) {
        setResetPasswordResult(newPassword);
      } else {
        alert('パスワードの取得に失敗しました。');
      }
    } catch {
      alert('パスワードのリセットに失敗しました。');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCopyPassword = async (password: string) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch {
      alert('コピーに失敗しました');
    }
  };

  const handleEdit = (admin: AdminAccount) => {
    setFormData({
      name: admin.name,
      contactNumber: admin.contactNumber,
      email: admin.email,
    });
    setEditingId(admin.id);
    setCreatedPassword(null);
    setResetPasswordResult(null);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDelete = async (admin: AdminAccount) => {
    try {
      await deleteAdmin(admin.email);
      if (editingId === admin.id) handleClear();
      loadAdmins();
    } catch {
      alert('削除に失敗しました');
    }
  };

  return (
    <div className="admin-management">
      <h1 className="admin-management__title">管理者</h1>

      <section ref={formRef} className="admin-management__form-panel">
        <h2 className="admin-management__form-heading">
          <img src={addNewIcon} alt="" className="new-reg-modal__title-icon" aria-hidden />
          {editingId ? '管理者を編集' : '新規登録'}
        </h2>

        <form onSubmit={(e) => { e.preventDefault(); void handleSave(); }}>
          <div className="new-reg-modal__form-section admin-management__form-section">
            <div className="new-reg-modal__form-row">
              <div className="new-reg-modal__form-group">
                <label>氏名</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="氏名を入力"
                />
              </div>
              <div className="new-reg-modal__form-group">
                <label>連絡先番号</label>
                <input
                  type="tel"
                  value={formData.contactNumber}
                  onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                  placeholder="連絡先番号を入力"
                />
              </div>
              <div className="new-reg-modal__form-group">
                <label>メールアドレス</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="メールアドレスを入力"
                  readOnly={!!editingId}
                />
              </div>
            </div>

            {editingId ? (
              <div className="new-reg-modal__reset-pwd-section">
                {resetPasswordResult ? (
                  <div className="new-reg-modal__reset-pwd-result">
                    <span className="new-reg-modal__reset-pwd-label">新しいパスワード:</span>
                    <span className="new-reg-modal__reset-pwd-value">{resetPasswordResult}</span>
                    <button
                      type="button"
                      className="new-reg-modal__btn new-reg-modal__btn--copy"
                      onClick={() => void handleCopyPassword(resetPasswordResult)}
                    >
                      {copyFeedback ? 'コピーしました' : 'コピー'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="new-reg-modal__btn new-reg-modal__btn--reset-pwd"
                    onClick={() => void handleResetPassword()}
                    disabled={isResettingPassword}
                  >
                    {isResettingPassword ? 'リセット中...' : 'パスワードをリセット'}
                  </button>
                )}
              </div>
            ) : createdPassword ? (
              <div className="new-reg-modal__reset-pwd-result">
                <span className="new-reg-modal__reset-pwd-label">発行されたパスワード:</span>
                <span className="new-reg-modal__reset-pwd-value">{createdPassword}</span>
                <button
                  type="button"
                  className="new-reg-modal__btn new-reg-modal__btn--copy"
                  onClick={() => void handleCopyPassword(createdPassword)}
                >
                  {copyFeedback ? 'コピーしました' : 'コピー'}
                </button>
              </div>
            ) : (
              <p className="new-reg-modal__password-note">
                パスワードは登録時に自動発行されます（英数字10文字）。管理者へお伝えください。
              </p>
            )}

            <div className="new-reg-modal__actions">
              <button
                type="submit"
                className="new-reg-modal__btn new-reg-modal__btn--save"
                disabled={isSubmitting}
              >
                {editingId ? '更新' : '保存'}
              </button>
              <button
                type="button"
                className="new-reg-modal__btn new-reg-modal__btn--clear"
                onClick={handleClear}
              >
                クリア
              </button>
            </div>
          </div>
        </form>
      </section>

      <section className="admin-management__list-section">
        <div className="admin-management__controls">
          <h2 className="admin-management__list-title">登録済み一覧</h2>
          <form className="admin-management__search" onSubmit={handleSearch}>
            <input
              type="text"
              className="admin-management__search-input"
              placeholder="氏名またはメールで検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="admin-management__search-btn">
              検索
            </button>
          </form>
        </div>

        {loadError && (
          <p className="admin-management__error" role="alert">
            {loadError}
          </p>
        )}

        {loading ? (
          <p className="admin-management__loading">読み込み中…</p>
        ) : (
          <AdminDataTable
            admins={filteredAdmins}
            onEditClick={handleEdit}
            onDeleteClick={(admin) => void handleDelete(admin)}
          />
        )}
      </section>
    </div>
  );
};

export default AdminManagement;
